export const WEB = "https://azzirevents.com";
export const APP_STORE_URL = "https://apps.apple.com/app/id6449482277";
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.azzir";
/** Opens the App Store app (not Safari) when Universal Links did not claim /get. */
export const IOS_STORE_APP = "itms-apps://apps.apple.com/app/id6449482277";
/** Opens the Play Store app when App Links did not claim /get. */
export const ANDROID_STORE_APP = "market://details?id=com.azzir";

const DEEP_LINK_RE = /^(artists|clubs|events|ref)\/[A-Za-z0-9._~-]+$/i;

export function sanitizeRef(raw) {
  if (raw == null) return "";
  const value = String(raw).trim();
  if (!value || value.length > 128) return "";
  if (/[/\\:]/.test(value)) return "";
  return value;
}

export function sanitizeDeepLinkValue(raw) {
  if (raw == null) return "";
  const value = String(raw).trim().replace(/^\//, "");
  if (!value || value.length > 256) return "";
  if (value.includes("..") || value.includes("//") || /[:\\]/.test(value)) {
    return "";
  }
  if (!DEEP_LINK_RE.test(value)) return "";
  return value;
}

export function isIPadDevice({ userAgent = "", platform = "", maxTouchPoints = 0 } = {}) {
  const ua = userAgent || "";
  return (
    /iPad/i.test(ua) ||
    (platform === "MacIntel" && maxTouchPoints > 1)
  );
}

export function isMacDesktop({ userAgent = "", platform = "", maxTouchPoints = 0 } = {}) {
  const ua = userAgent || "";
  return /Macintosh/i.test(ua) && !isIPadDevice({ userAgent: ua, platform, maxTouchPoints });
}

export function isPhoneOrTablet({ userAgent = "", platform = "", maxTouchPoints = 0 } = {}) {
  const ua = userAgent || "";
  if (isMacDesktop({ userAgent: ua, platform, maxTouchPoints })) return false;
  return (
    /Android/i.test(ua) ||
    /iPhone|iPod/i.test(ua) ||
    isIPadDevice({ userAgent: ua, platform, maxTouchPoints })
  );
}

export function getClickPlatform(input = {}) {
  const ua = input.userAgent || "";
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPod/i.test(ua) || isIPadDevice(input)) return "ios";
  return null;
}

export function buildMobileUrl({ userAgent = "" } = {}) {
  if (/Android/i.test(userAgent || "")) {
    return ANDROID_STORE_APP;
  }
  return IOS_STORE_APP;
}

export function getHttpsStoreFallback({
  userAgent = "",
  platform = "",
  maxTouchPoints = 0,
} = {}) {
  if (!isPhoneOrTablet({ userAgent, platform, maxTouchPoints })) {
    return "";
  }
  if (/Android/i.test(userAgent || "")) {
    return PLAY_STORE_URL;
  }
  return APP_STORE_URL;
}

export function buildDesktopUrl({ ref, deepLinkValue } = {}) {
  const safeRef = sanitizeRef(ref);
  if (safeRef) {
    return `${WEB}/ref/${encodeURIComponent(safeRef)}`;
  }
  const dlv = sanitizeDeepLinkValue(deepLinkValue);
  if (dlv) {
    return `${WEB}/${dlv}`;
  }
  return WEB;
}

export function getOpenAppDestination({
  userAgent = "",
  platform = "",
  maxTouchPoints = 0,
  ref,
  deepLinkValue,
} = {}) {
  const device = { userAgent, platform, maxTouchPoints };

  if (isPhoneOrTablet(device)) {
    return buildMobileUrl({ userAgent });
  }

  return buildDesktopUrl({ ref, deepLinkValue });
}

/**
 * Automatically launches the installed Azzir app or redirects to Play Store / App Store
 * for mobile visitors (Android / iOS) opening entity links (events, clubs, artists).
 * Desktop / Mac users remain on the website without interruption.
 */
export function triggerMobileAppOrStoreRedirect({ entityType, entityId } = {}) {
  if (typeof window === "undefined" || !navigator) return "desktop";

  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !/Macintosh/i.test(ua);
  if (!isAndroid && !isIOS) return "desktop";

  const type = entityType ? String(entityType).toLowerCase() : "";
  const id = entityId ? String(entityId).trim() : "";
  const targetPath = type && id ? `${type}/${encodeURIComponent(id)}` : "get";

  // Prevent infinite redirect loops if user navigates back from the store
  const sessionKey = `azzir_redirect_${targetPath}`;
  try {
    if (sessionStorage.getItem(sessionKey)) {
      return "skipped";
    }
    sessionStorage.setItem(sessionKey, "1");
  } catch {
    // ignore sessionStorage errors in strict private browsing
  }

  let left = false;
  const onVisibilityChange = () => {
    if (document.hidden) left = true;
  };
  const onPageHide = () => {
    left = true;
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", onPageHide);

  const cleanup = () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", onPageHide);
  };

  if (isAndroid) {
    // 1. Try launching native app via custom scheme (handles in-app browsers)
    window.location.href = `azzir://${targetPath}`;

    // 2. Chrome intent fallback
    window.setTimeout(() => {
      if (left) return;
      window.location.href = `intent://${targetPath}#Intent;scheme=azzir;package=com.azzir;end`;
    }, 300);

    // 3. Fallback to Google Play Store if app didn't open
    window.setTimeout(() => {
      cleanup();
      if (!left) {
        window.location.href = PLAY_STORE_URL;
      }
    }, 1200);

    return "mobile";
  }

  // iOS:
  // 1. Try launching native app via custom scheme
  window.location.href = `azzir://${targetPath}`;

  // 2. Fallback to Apple App Store if app didn't open
  window.setTimeout(() => {
    cleanup();
    if (!left) {
      window.location.href = APP_STORE_URL;
    }
  }, 1200);

  return "mobile";
}

